/**
 * The seam between the screens and their data.
 *
 * The project and the crew now come from the API — `useProject` and `useCrew`
 * are real ts-rest queries, and the `PROJECT` and `CREW` fixtures are gone with
 * their types living in `@gb/contracts`. The schedule and the site manager
 * still resolve synchronously from `@/fixtures` until their endpoints land;
 * each of those hooks keeps the shape a TanStack Query result exposes for the
 * fields screens actually read, so swapping one is still a change to this file
 * alone.
 *
 * Compliance is no longer derived here. It is computed once on the server and
 * arrives on the roster — see `apps/api/src/crew/crew-compliance.ts` for why.
 */
import { useMemo } from "react";
import type { CrewMember, CrewRoster, Project } from "@gb/contracts";
import { useRoster } from "@/features/crew/crew-api";
import { SCHEDULE, SITE_MANAGER } from "@/fixtures/site";
import type { ScheduleItem, SiteManager } from "@/fixtures/types";
import { useProjectContext } from "@/app/project-context";

export type { Project };

/**
 * The project the current URL is about.
 *
 * Non-nullable. `ProjectLayout` resolves `/projects/:projectId` and renders its
 * own pending / not-found / error states, so a screen that is mounted at all is
 * a screen with a project. This used to return `Project | null` and every
 * caller carried a null branch; those branches now live in one place.
 *
 * Calling this outside a project route throws rather than returning null —
 * silently rendering a screen with no site is how the wrong data gets shown.
 */
export function useProject(): Project {
  return useProjectContext().project;
}

/** The project name for the chrome (sidebar eyebrow, greeting line). */
export function useProjectName(): string {
  return useProject().name;
}

export function useSiteManager(): SiteManager {
  return SITE_MANAGER;
}

export function useSchedule(): ScheduleItem[] {
  return SCHEDULE;
}

export type CrewData = {
  /** Everyone with a period at this project, whatever their state. */
  all: CrewMember[];
  /** Cleared and working today — what the compliance tiles count. */
  onSite: CrewMember[];
  onboarding: CrewMember[];
  offSite: CrewMember[];
  breakdown: CrewRoster["breakdown"];
  isPending: boolean;
};

const EMPTY: CrewData = {
  all: [],
  onSite: [],
  onboarding: [],
  offSite: [],
  breakdown: { valid: 0, expiring: 0, missing: 0 },
  isPending: true,
};

export function useCrew(): CrewData {
  const { projectId } = useProjectContext();
  const { data, isPending } = useRoster(projectId);

  return useMemo(() => {
    if (!data) return { ...EMPTY, isPending };
    return {
      all: [...data.onSite, ...data.onboarding, ...data.offSite],
      onSite: data.onSite,
      onboarding: data.onboarding,
      offSite: data.offSite,
      breakdown: data.breakdown,
      isPending,
    };
  }, [data, isPending]);
}

/**
 * One row per open flag across the crew working today, worst first.
 *
 * A flat list of *documents*, not people: a person short two certificates is
 * two things to chase, and the Today banner is a to-do list. Onboarding and
 * off-site crew are excluded for the same reason the tiles exclude them.
 */
export type ComplianceAlert = {
  personId: string;
  personName: string;
  role: string;
  label: string;
  status: CrewMember["overall"];
  days: number | null;
};

const RANK: Record<CrewMember["overall"], number> = {
  missing: 0,
  expired: 1,
  expiring: 2,
  valid: 3,
};

export function useComplianceAlerts(): ComplianceAlert[] {
  const { onSite } = useCrew();

  return useMemo(() => {
    const out: ComplianceAlert[] = [];
    for (const person of onSite) {
      for (const doc of person.perDoc) {
        if (doc.status === "valid") continue;
        out.push({
          personId: person.id,
          personName: person.name,
          role: person.roleLabel,
          label: doc.label,
          status: doc.status,
          days: doc.days,
        });
      }
    }
    // Worst first, then soonest. A missing document has no expiry, so it sorts
    // to the top rather than to the bottom on a null.
    const key = (a: ComplianceAlert) => (a.days === null ? -1e9 : a.days);
    out.sort((a, b) => RANK[a.status] - RANK[b.status] || key(a) - key(b));
    return out;
  }, [onSite]);
}

/** The banner tag copy differs from the pill label for the amber case. */
export function alertTagLabel(status: CrewMember["overall"]): string {
  if (status === "missing") return "Missing";
  if (status === "expired") return "Expired";
  return "Due soon";
}

/** "not on file" / "12d ago" / "9d left". */
export function alertStatusText(alert: ComplianceAlert): string {
  if (alert.status === "missing" || alert.days === null) return "not on file";
  if (alert.status === "expired") return `${Math.abs(alert.days)}d ago`;
  return `${alert.days}d left`;
}
